import React, { ComponentProps, useCallback, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useUser } from "@hooks/useUser";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { isConnectionOwner } from "@hooks/useConnectionOwnership";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { UpdateFinancialAccountEntity } from "@components/financialAccount/UpdateFinancialAccountEntity";
import { useEntityChangeWithWarnings } from "@components/financialAccount/useEntityChangeWithWarnings";
import { FinancialAccountTypeSelect } from "@components/financialAccount/FinancialAccountTypeSelect";
import { useChangeFinancialAccountEntityMutation } from "@mutations/financialAccount";
import { StandardModal, StandardModalBody } from "@design-system/Modal/Modal";

interface AccountTypeChangeProps {
    account: FinancialAccount;
    connection?: FinancialConnection | IntegrationConnection;
    showIcon?: boolean;
    showLabel?: boolean;
    onChanged?: () => void;
    disabled?: boolean;
    size?: ComponentProps<typeof FinancialAccountTypeSelect>["size"];
    className?: string;
}

export const AccountTypeChange: React.FC<AccountTypeChangeProps> = ({
    account,
    connection,
    showIcon = true,
    showLabel = true,
    onChanged,
    disabled,
    size,
    className,
}) => {
    const user = useUser();
    const canEditEntitySettings = useCanPerformWorkspaceAction(
        WorkspaceAction.ENTITY_SETTINGS_EDIT,
    );
    const isOwner = connection ? isConnectionOwner(connection, user.id) : true;
    const canChangeType = isOwner || canEditEntitySettings;
    const tooltipReason = useViewOnlyTooltipReason(
        "Only the user who connected this account or a workspace admin/accountant can change its type",
    );
    const { personalEntities, nonMockBusinessEntities } =
        useFinancialAccountEntitySelection();
    const [showEntitySelection, setShowEntitySelection] = useState<{
        isBusiness: boolean;
    }>();
    const changeEntity = useChangeFinancialAccountEntityMutation(
        account,
        onChanged,
    );

    const { initiateEntityChange, confirmationModal, isVerifyingEntityChange } =
        useEntityChangeWithWarnings({
            account,
            mutation: changeEntity,
        });

    const handleChangeAccountType = useCallback(
        async (isBusiness: boolean) => {
            const entities = isBusiness
                ? nonMockBusinessEntities
                : personalEntities;

            if (entities.length === 1) {
                await initiateEntityChange(entities[0]);
            } else {
                setShowEntitySelection({ isBusiness });
            }
        },
        [nonMockBusinessEntities, personalEntities, initiateEntityChange],
    );

    const onUpdatedViaModal = useCallback(() => {
        setShowEntitySelection(undefined);
        onChanged?.();
    }, [onChanged]);

    return (
        <>
            <Tooltip disabled={canChangeType} content={tooltipReason}>
                <div className={className}>
                    <FinancialAccountTypeSelect
                        value={account.isBusiness}
                        onChange={handleChangeAccountType}
                        loading={
                            changeEntity.isPending || isVerifyingEntityChange
                        }
                        disabled={!canChangeType || disabled}
                        showIcon={showIcon}
                        showLabel={showLabel}
                        size={size}
                    />
                </div>
            </Tooltip>
            <StandardModal
                show={Boolean(showEntitySelection)}
                onHide={() => setShowEntitySelection(undefined)}
            >
                <StandardModalBody>
                    <p>
                        Select{" "}
                        {showEntitySelection?.isBusiness ? "business" : "where"}{" "}
                        you'd like to assign this account to:
                    </p>
                    <UpdateFinancialAccountEntity
                        account={account}
                        connection={connection}
                        isBusiness={showEntitySelection?.isBusiness}
                        onUpdated={onUpdatedViaModal}
                    />
                </StandardModalBody>
            </StandardModal>

            {confirmationModal}
        </>
    );
};
